/**
 * Funder interface agents (TRUS-778) — every call the Agents page's
 * "Funder interfaces" tab makes, in one place.
 *
 *   /api/admin/funder-agents/*            plans, sources, source suggestions, runs, rules, findings
 *   /api/admin/funder-rules/proposals*    the human gate: list, create, approve, reject
 *   /api/platform/v1/agents/{key}/...     schedule + Run now (agentsApi, re-exported)
 *
 * All platform-admin routes on weavein-api. They go through authFetchJson +
 * getWeaveinApiUrl (never a relative /api path — on weavein.ai that is
 * rewritten to the development backend). The backend wraps lists
 * ({plans: [...]}, {findings: [...]}); `unwrap` takes them apart here, once,
 * and tolerates a bare array so a later envelope change is one edit.
 */
import { authFetchJson } from '@/lib/authFetch';
import { getWeaveinApiUrl } from '@/src/config';
import { agentsApi } from './platformApi';

export const BUILDER_KEY = 'weavein.funder_rules_builder';
export const HARNESS_KEY = 'weavein.funder_interface_harness';
export const FINDER_KEY = 'weavein.funder_source_finder';

export const PROGRAM_TYPES = ['ECM', 'CS', 'CHW', 'Doula', 'ALL'] as const;
export type ProgramType = (typeof PROGRAM_TYPES)[number];

export type AgentName = 'builder' | 'harness' | 'finder';
export type RunStatus = 'queued' | 'running' | 'completed' | 'failed';
export type Severity = 'high' | 'medium' | 'low';
export type FindingKind =
  | 'conflict'
  | 'invalid_value'
  | 'missing_source'
  | 'source_changed'
  | 'source_unreachable'
  | 'expiring'
  | 'denial_pattern';
export type RuleClass = 'generic' | 'statewide' | 'plan_specific';

export interface FunderSummary {
  plans_with_sources: number;
  sources_enabled: number;
  proposals_pending: number;
  /** Documents the source finder found, waiting for a person (absent before the finder shipped). */
  suggestions_pending?: number;
  findings_open: { high: number; medium: number; low: number };
  last_runs: Partial<Record<AgentName, { status: RunStatus; finished_at: string | null } | null>>;
}

export interface FunderPlan {
  payer_id: number;
  payer_name: string;
  payer_type?: string | null;
  states?: string[] | null;
  edi_payer_id?: string | null;
  rules: { generic: number; statewide: number; plan_specific: number };
  sources: number;
  sources_enabled: number;
  proposals_pending: number;
  suggestions_pending?: number;
  findings_open: number;
  last_drafted_at?: string | null;
}

export interface DirectoryPayer {
  id: number;
  display_name: string;
  primary_payer_id?: string | null;
  operating_states?: string[] | null;
  programs?: string[] | null;
  payer_id?: number | null;
}

export interface FunderSource {
  id: number;
  payer_id: number;
  program_type: ProgramType;
  source_url: string;
  label?: string | null;
  enabled: boolean;
  last_fetched_at?: string | null;
  last_status?: string | null;
  last_error?: string | null;
  consecutive_failures?: number | null;
  last_drafted_at?: string | null;
}

export interface FunderRun {
  id: number;
  agent: AgentName;
  /** POST /runs → manual; Run now from the registry → schedule; a finding's Fix → fix. */
  trigger: 'schedule' | 'manual' | 'run_now' | 'fix' | string;
  requested_by?: number | null;
  payer_ids?: number[] | null;
  force?: boolean;
  status: RunStatus;
  /** Queued or running with no progress for 15 minutes: the worker lost it. It can be run again. */
  stale?: boolean;
  started_at?: string | null;
  finished_at?: string | null;
  stats?: {
    /** Set on a run started with Run again: the run it repeats. */
    retry_of?: number;
    sources_fetched?: number;
    sources_unchanged?: number;
    sources_failed?: number;
    proposals_created?: number;
    // source finder
    plans_searched?: number;
    suggested?: number;
    dropped?: number;
    errors?: number;
    findings_opened?: number;
    findings_resolved?: number;
  } | null;
  error?: string | null;
  created_at?: string | null;
}

/** A document the source finder found for a plan. Nothing reads it until a person accepts it. */
export interface SourceSuggestion {
  id: number;
  payer_id: number;
  payer_name: string;
  program_type: ProgramType;
  source_url: string;
  title?: string | null;
  /** The agent's one sentence on what the document is. */
  reason?: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  run_id?: number | null;
  source_id?: number | null;
  note?: string | null;
  created_at?: string | null;
}

export interface FunderRule {
  id: number;
  payer_id: number;
  program_type: string;
  rule_key: string;
  rule_value: unknown;
  class: RuleClass;
  effective_date?: string | null;
  expiration_date?: string | null;
  source_url?: string | null;
  excerpt?: string | null;
  proposal_id?: number | null;
}

export interface FunderFinding {
  id: number;
  payer_id: number;
  payer_name?: string | null;
  program_type?: string | null;
  rule_id?: number | null;
  source_id?: number | null;
  kind: FindingKind;
  severity: Severity;
  message: string;
  detail?: Record<string, unknown> | null;
  status: 'open' | 'fixing' | 'resolved' | 'dismissed';
  proposal_id?: number | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
}

export interface RuleProposal {
  id: number;
  payer_id: number;
  payer_name: string;
  program_type: string;
  rule_key: string;
  rule_value: unknown;
  effective_date?: string | null;
  source_url?: string | null;
  source_excerpt?: string | null;
  rationale?: string | null;
  proposed_by: 'agent' | 'user';
  status: 'pending' | 'approved' | 'rejected';
  decision_note?: string | null;
  created_ts?: string | null;
}

/**
 * What POST /findings/{id}/fix answers:
 *   redraft      a builder run was queued for the plan (finding → fixing)
 *   needs_source the plan has no enabled source; add one first
 *   edit_source  the source stopped answering; fix its URL
 *   edit_rules   the rules involved; a person proposes the correction
 */
export interface FixResult {
  action: 'redraft' | 'needs_source' | 'edit_source' | 'edit_rules';
  message?: string;
  run?: FunderRun | null;
  source?: FunderSource | null;
  rules?: FunderRule[];
}

/** PATCH /rules/{id} answers with the proposal it opened; nothing changes until it is approved. */
export interface RuleEditResult {
  proposal_id: number;
  effective_date?: string | null;
  supersedes_rule_id?: number | null;
}

const AGENTS = '/api/admin/funder-agents';
const RULES = '/api/admin/funder-rules';
const url = (path: string) => getWeaveinApiUrl(path);

/** Take a list out of its envelope; a bare array passes through; anything else is empty. */
export function unwrap<T>(key: string, data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  const list = (data as Record<string, unknown> | null | undefined)?.[key];
  return Array.isArray(list) ? (list as T[]) : [];
}

const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

const get = <T>(path: string) => authFetchJson<T>(url(path));
const send = <T>(method: 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown) =>
  authFetchJson<T>(url(path), { method, jsonBody: body ?? {} });

export interface PlanFilters {
  q?: string;
  state?: string;
  has_sources?: boolean;
  has_findings?: boolean;
  limit?: number;
  offset?: number;
}

export const funderAgentsApi = {
  summary: () => get<FunderSummary>(`${AGENTS}/summary`),

  plans: async (f: PlanFilters = {}) => {
    const data = await get<unknown>(
      `${AGENTS}/plans${qs({
        q: f.q,
        state: f.state,
        has_sources: f.has_sources ? true : undefined,
        has_findings: f.has_findings ? true : undefined,
        limit: f.limit,
        offset: f.offset,
      })}`,
    );
    const total = (data as { total?: number } | null)?.total;
    const plans = unwrap<FunderPlan>('plans', data);
    return { plans, total: typeof total === 'number' ? total : plans.length };
  },
  linkPlan: (directoryId: number) => send<FunderPlan>('POST', `${AGENTS}/plans/link`, { directory_id: directoryId }),
  searchDirectory: async (q: string, state?: string) =>
    unwrap<DirectoryPayer>('payers', await get<unknown>(`${AGENTS}/directory${qs({ q, state, program: 'MEDICAID' })}`)),

  sources: async (payerId: number) =>
    unwrap<FunderSource>('sources', await get<unknown>(`${AGENTS}/sources${qs({ payer_id: payerId })}`)),
  addSource: (body: { payer_id: number; program_type: ProgramType; source_url: string; label?: string }) =>
    send<FunderSource>('POST', `${AGENTS}/sources`, body),
  updateSource: (id: number, patch: Partial<Pick<FunderSource, 'enabled' | 'label' | 'source_url'>>) =>
    send<FunderSource>('PATCH', `${AGENTS}/sources/${id}`, patch),
  deleteSource: (id: number) => send<unknown>('DELETE', `${AGENTS}/sources/${id}`),

  suggestions: async (f: { status?: SourceSuggestion['status'] | 'all'; payer_id?: number } = {}) =>
    unwrap<SourceSuggestion>(
      'suggestions',
      await get<unknown>(`${AGENTS}/source-suggestions${qs({ status: f.status ?? 'pending', payer_id: f.payer_id })}`),
    ),
  /** Register the document as the plan's source; `draft` also queues a rules builder run for the plan. */
  acceptSuggestion: (id: number, body: { program_type?: ProgramType; draft?: boolean } = {}) =>
    send<{ candidate_id: number; source: FunderSource; run?: FunderRun }>('POST', `${AGENTS}/source-suggestions/${id}/accept`, body),
  rejectSuggestion: (id: number, note: string) => {
    if (!note.trim()) return Promise.reject(new Error('A reason is required to reject a suggestion.'));
    return send<unknown>('POST', `${AGENTS}/source-suggestions/${id}/reject`, { note });
  },

  startRun: (body: { agent: AgentName; payer_ids?: number[]; force?: boolean }) =>
    send<{ run?: FunderRun } & Partial<FunderRun>>('POST', `${AGENTS}/runs`, body).then(
      (r) => (r && 'run' in r && r.run ? r.run : (r as FunderRun)),
    ),
  /** Run a failed, completed or stuck run again on the same plans (409 while it is still in progress). */
  retryRun: (id: number) =>
    send<{ run?: FunderRun } & Partial<FunderRun>>('POST', `${AGENTS}/runs/${id}/retry`).then(
      (r) => (r && 'run' in r && r.run ? r.run : (r as FunderRun)),
    ),
  runs: async (agent?: AgentName, limit = 25) =>
    unwrap<FunderRun>('runs', await get<unknown>(`${AGENTS}/runs${qs({ agent, limit })}`)),

  rules: async (payerId: number) =>
    unwrap<FunderRule>('rules', await get<unknown>(`${AGENTS}/rules${qs({ payer_id: payerId, active: true })}`)),
  editRule: (id: number, body: { rule_value: unknown; effective_date?: string; note: string; finding_id?: number }) =>
    send<RuleEditResult>('PATCH', `${AGENTS}/rules/${id}`, body),
  expireRule: (id: number, body: { note: string; expiration_date?: string }) =>
    send<FunderRule>('POST', `${AGENTS}/rules/${id}/expire`, body),

  findings: async (f: { status?: FunderFinding['status'] | 'all'; payer_id?: number; kind?: FindingKind; severity?: Severity } = {}) =>
    unwrap<FunderFinding>(
      'findings',
      await get<unknown>(`${AGENTS}/findings${qs({ ...f, status: f.status ?? 'open' })}`),
    ),
  fixFinding: (id: number) => send<FixResult>('POST', `${AGENTS}/findings/${id}/fix`),
  dismissFinding: (id: number, note: string) => send<FunderFinding>('POST', `${AGENTS}/findings/${id}/dismiss`, { note }),

  // The human gate — the existing TRUS-775 proposal routes.
  proposals: async (f: { status?: string; payer_id?: number } = {}) =>
    unwrap<RuleProposal>('proposals', await get<unknown>(`${RULES}/proposals${qs({ status: f.status ?? 'pending', payer_id: f.payer_id })}`)),
  createProposal: (body: {
    payer_id: number;
    program_type: string;
    rule_key: string;
    rule_value: unknown;
    effective_date?: string | null;
    source_url?: string | null;
    source_excerpt?: string | null;
    rationale?: string | null;
  }) => send<{ id: number; status: string }>('POST', `${RULES}/proposals`, body),
  approve: (id: number, note?: string) => send<unknown>('POST', `${RULES}/proposals/${id}/approve`, { note: note || null }),
  reject: (id: number, note: string) => {
    if (!note.trim()) return Promise.reject(new Error('A reason is required to reject a proposal.'));
    return send<unknown>('POST', `${RULES}/proposals/${id}/reject`, { note });
  },

  // Schedule + Run now go through the existing agent registry (platform API envelope).
  registry: async () => {
    const res = await agentsApi.getRegistry('weavein');
    if (!res.success) throw new Error(res.error?.message || 'Could not load the agent catalog');
    const keys = [BUILDER_KEY, HARNESS_KEY, FINDER_KEY];
    return (res.data ?? []).filter((a: { key?: string }) => keys.includes(a.key ?? ''));
  },
  setSchedule: async (key: string, body: { cron_expr?: string; enabled?: boolean }) => {
    const res = await agentsApi.setAgentSchedule(key, body);
    if (!res.success) throw new Error(res.error?.message || 'Could not save the schedule');
    return res.data;
  },
  runNow: async (key: string) => {
    const res = await agentsApi.runAgent(key);
    if (!res.success) throw new Error(res.error?.message || 'Could not start the run');
    return res.data;
  },
};

export type FunderAgentsApi = typeof funderAgentsApi;
